<template>
    <div class="p-3 sm:p-6">
        <DataTableV2
            v-if="isAdmin || isOwner"
            title="Invitations"
            :headers="headers"
            :items="invitations"
            searchable
        >
            <template #icon>
                <UserPlus class="size-6 text-new-neutral-800" />
            </template>

            <template #toolbar>
                <div class="flex items-center gap-2">
                    <Button
                        v-if="isOwner || isAdmin"
                        variant="default"
                        size="sm"
                        @click="inviteDialogOpen = true"
                    >
                        <UserPlus class="size-4" />
                        Invite member
                    </Button>

                    <Button
                        variant="outline"
                        size="sm"
                        as="a"
                        :href="route('members.index')"
                        @click.prevent="router.get(route('members.index'))"
                    >
                        Back to members
                    </Button>
                </div>
            </template>

            <template #item-email="{ data }">
                <span class="text-sm font-medium text-foreground">{{ data }}</span>
            </template>

            <template #item-token="{ data }">
                <span v-if="data" class="text-sm text-muted-foreground">
                    {{ teamProps.name }}
                </span>
            </template>

            <template #item-created_at="{ data, row }">
                <div class="flex items-center gap-2">
                    <Badge variant="warning" class="gap-1.5 rounded-none">
                        <span class="size-1.5 rounded-full bg-new-orange-500"></span>
                        Pending ({{ pendingDays(data as string) }}
                        {{ pendingDays(data as string) !== 1 ? 'days' : 'day' }})
                    </Badge>
                    <button
                        class="text-sm font-medium text-new-blue-600 hover:text-new-blue-700 hover:underline"
                        @click="remindInvitationFromRow(row as IInvitation)"
                    >
                        Remind
                    </button>
                </div>
            </template>

            <template #item-role="{ data }">
                <Badge variant="secondary" class="rounded-none">
                    {{ roleLabel(data as string) }}
                </Badge>
            </template>

            <template #item-actions="{ row }">
                <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                        <Button variant="ghost" size="icon" class="size-8">
                            <MoreVertical class="size-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem @click="remindInvitationFromRow(row as IInvitation)">
                            <Send class="size-4" />
                            Resend invitation
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            variant="destructive"
                            @click="openDeleteDialog(row as IInvitation)"
                        >
                            <Trash2 class="size-4" />
                            Delete invitation
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </template>

            <template #empty>
                <div class="flex flex-col items-center justify-center py-8 gap-4">
                    <UserPlus class="size-10 text-muted-foreground/50" />
                    <div class="flex flex-col items-center gap-1">
                        <p class="text-sm font-semibold text-foreground">No pending invitations</p>
                        <p class="text-xs text-muted-foreground">
                            All invitations have been accepted or expired.
                        </p>
                    </div>
                    <Button
                        v-if="isOwner || isAdmin"
                        variant="default"
                        size="sm"
                        @click="inviteDialogOpen = true"
                    >
                        <UserPlus class="size-4" />
                        Invite member
                    </Button>
                </div>
            </template>

        </DataTableV2>

        <InviteMemberDialog
            :open="inviteDialogOpen"
            :roles="inviteRoleOptions"
            :is-personal-team="teamProps.personal_team"
            @close="inviteDialogOpen = false"
        />

        <Dialog :open="!!pendingDelete" @update:open="(val) => !val && (pendingDelete = null)">
            <DialogContent class="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete invitation</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete the invitation for
                        <span class="font-medium text-foreground">{{ pendingDelete?.email }}</span>?
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter class="mt-4">
                    <Button variant="outline" :disabled="deleteLoading" @click="pendingDelete = null">Cancel</Button>
                    <Button variant="destructive" :disabled="deleteLoading" @click="confirmDelete">Delete</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from 'ziggy-js'
import { differenceInCalendarDays } from 'date-fns'
import { UserPlus, MoreVertical, Trash2, Send } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

import { DataTableV2 } from '@/components/ui/data-table-v2'
import type { IDataTableHeader, IPaginationData } from '@/components/ui/data-table-v2'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue'
import usePermissions from '@/composables/usePermissions'
import IInvitation from '@/types/Members/IInvitation'
import ICurrentTeam from '@/types/Members/ICurrentTeam'
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps'

import InviteMemberDialog from './components/InviteMemberDialog.vue'

defineOptions({
    layout: DefaultLayoutV2,
})

defineProps<{
    invitations: IPaginationData<IInvitation>
}>()

const { isAdmin, isOwner } = usePermissions()
const globalProps = computed(() => usePage<ITopolPageProps>().props)
const teamProps = computed(() => globalProps.value.team as ICurrentTeam)

const inviteDialogOpen = ref(false)
const pendingDelete = ref<IInvitation | null>(null)
const deleteLoading = ref(false)

const inviteRoleOptions = computed(() => {
    if (teamProps.value.personal_team) {
        return [{ label: 'Admin', value: 'viewer' }]
    }

    return [
        { label: 'Administrator', value: 'admin' },
        { label: 'Editor', value: 'editor' },
        { label: 'Admin', value: 'viewer' },
    ]
})

const roleLabel = (role: string) => {
    if (role === 'viewer') return 'Admin'
    return role.charAt(0).toUpperCase() + role.slice(1)
}

const headers: IDataTableHeader[] = [
    { text: 'Email', id: 'email' },
    { text: 'Team', id: 'token' },
    { text: 'Status', id: 'created_at' },
    { text: 'Role', id: 'role', align: 'left' },
    { text: '', id: 'actions' },
]

const pendingDays = (dateStr: string) => {
    return differenceInCalendarDays(new Date(), new Date(dateStr))
}

const remindInvitationFromRow = (invitation: IInvitation) => {
    router.post(
        route('members.store'),
        { email: invitation.email, role: invitation.role },
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Invitation reminded')
            },
            onError: () => {
                toast.error('There was an error reminding your invitation.')
            },
        }
    )
}

const openDeleteDialog = (invitation: IInvitation) => {
    pendingDelete.value = invitation
}

const confirmDelete = () => {
    const invitation = pendingDelete.value
    if (!invitation) return

    deleteLoading.value = true
    router.get(
        route('team.invitation.refuse', { token: invitation.token }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Invitation deleted')
                pendingDelete.value = null
            },
            onError: () => {
                toast.error('There was an error deleting your invitation.')
            },
            onFinish: () => {
                deleteLoading.value = false
            },
        }
    )
}
</script>
